import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom"

import { RequireAuth } from "@/components/RequireAuth"
import { AppLayout } from "@/components/layout/AppLayout"
import { RequireEntity } from "@/components/layout/RequireEntity"
import { RequireSeccion } from "@/components/layout/RequireSeccion"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/toaster"
import { AuthProvider } from "@/lib/auth"

import { DeclaradoPage } from "@/routes/finance/DeclaradoPage"
import { LoginPage } from "@/routes/login/LoginPage"
import { RegistroPage } from "@/routes/registro/RegistroPage"
import { AccesosPage } from "@/routes/accesos/AccesosPage"
import { InvitacionPage } from "@/routes/accesos/InvitacionPage"
import { ReviewQueuePage } from "@/routes/review/ReviewQueuePage"
import { BooksLayout } from "@/routes/books/BooksLayout"
import { PendientesPage } from "@/routes/comentarios/PendientesPage"
import { PlanillaPage } from "@/routes/payroll/PlanillaPage"
import { Entrada } from "@/routes/onboarding/Entrada"
import { PrimerosPasosPage } from "@/routes/onboarding/PrimerosPasosPage"
import { TraspasoLayout } from "@/routes/onboarding/TraspasoLayout"
import { BalanceSheetPage } from "@/routes/statements/BalanceSheetPage"
import { CashFlowStatementPage } from "@/routes/statements/CashFlowStatementPage"
import { IncomeStatementPage } from "@/routes/statements/IncomeStatementPage"
import { StatementsLayout } from "@/routes/statements/StatementsLayout"
import { JournalPage } from "@/routes/books/JournalPage"
import { LedgerPage } from "@/routes/books/LedgerPage"
import { TrialBalancePage } from "@/routes/books/TrialBalancePage"
import { CompareBooksPage } from "@/routes/books/CompareBooksPage"
import { CargaPage } from "@/routes/carga/CargaPage"
import { MastersLayout } from "@/routes/masters/MastersLayout"
import { PeriodosPage } from "@/routes/masters/PeriodosPage"
import { PlanGerencialPage } from "@/routes/masters/PlanGerencialPage"
import { SociedadesPage } from "@/routes/masters/SociedadesPage"
import { ClientsPage } from "@/routes/masters/ClientsPage"
import { SuppliersPage } from "@/routes/masters/SuppliersPage"
import { MovimientosBancariosPage } from "@/routes/masters/MovimientosBancariosPage"
import { ContractsPage } from "@/routes/masters/ContractsPage"
import { PeoplePage } from "@/routes/masters/PeoplePage"
import { CostCentersPage } from "@/routes/masters/CostCentersPage"
import { TreatmentRulesPage } from "@/routes/masters/TreatmentRulesPage"
import { AccountantConsolePage } from "@/routes/accountant/AccountantConsolePage"
import { ExpedientePage } from "@/routes/expediente/ExpedientePage"
import { ArrastresPage } from "@/routes/accountant/ArrastresPage"
import { AuditoriaPage } from "@/routes/auditoria/AuditoriaPage"
import { GestionDelGrupo } from "@/routes/holding/GestionDelGrupo"
import { HoldingLayout } from "@/routes/holding/HoldingLayout"
import { AnalisisPage } from "@/routes/holding/AnalisisPage"
import { ConsolidadoPage } from "@/routes/holding/ConsolidadoPage"
import { IntercompaniaPage } from "@/routes/holding/IntercompaniaPage"
import { ObligacionesPage } from "@/routes/holding/ObligacionesPage"
import { TesoreriaPage } from "@/routes/holding/TesoreriaPage"
import { ContabilidadLayout } from "@/routes/contabilidad/ContabilidadLayout"
import { DashboardPage } from "@/routes/dashboard/DashboardPage"
import { CashFlowPage } from "@/routes/finance/CashFlowPage"
import { DatosBasePage } from "@/routes/finance/DatosBasePage"
import { CuadresPage } from "@/routes/finance/CuadresPage"
import { QueVaAFinanzasPage } from "@/routes/finance/QueVaAFinanzasPage"
import { FinanceLayout } from "@/routes/finance/FinanceLayout"
import { PnlPage } from "@/routes/finance/PnlPage"
import { DesgloseDeCuentasPage } from "@/routes/finance/DesgloseDeCuentasPage"
import { CobranzasPage } from "@/routes/cobranza/CobranzasPage"
import { ForecastPage } from "@/routes/forecast/ForecastPage"
import { t } from "@/lib/i18n"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

/**
 * Pantalla que revienta a proposito.
 *
 * Existe para una sola cosa: que la prueba de navegador pueda provocar un error
 * de render DE VERDAD y comprobar que sale el `ErrorBoundary` y no una pantalla
 * en blanco. Un limite de error que nadie probo no es una red, es una intencion.
 *
 * Solo se enruta en desarrollo: en el bundle de produccion la ruta no se
 * registra.
 */
function Reventar(): never {
  throw new Error("Fallo provocado para verificar el limite de errores")
}

/** La ruta vieja del análisis de cuenta, con su `?cuenta=` conservado. */
function AnalisisAlMayor() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: "../mayor", search }} replace />
}

function NotFound() {
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center">
      <p className="code text-2xl font-semibold text-border-strong">404</p>
      <h1 className="titular punto mt-2 text-lg">{t("Esta pantalla no existe")}</h1>
      <p className="mt-1 text-xs leading-[1.15rem] text-muted-foreground">
        {t("Puede que el enlace sea de una versión anterior, o que la ruta pertenezca a un módulo que todavía no está habilitado para tu rol.")}</p>
      <div className="mt-5 flex justify-center gap-2">
        <Button asChild size="sm">
          <Link to="/dashboard">{t("Ir al dashboard")}</Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to="/contabilidad/libros">{t("Ir a los libros")}</Link>
        </Button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/*
              DOS PÁGINAS Y NO DOS PESTAÑAS. Entrar no escribe nada; registrarse
              crea un cliente, su plan gerencial, su mapeo, sus reglas y un rol.
              Cesar: «deberías tener dos páginas, registro y log in, que cumplen
              roles diferentes». Ver `routes/registro/RegistroPage.tsx`.
            */}
            <Route path="/registro" element={<RegistroPage />} />
            {import.meta.env.DEV ? <Route path="/_reventar" element={<Reventar />} /> : null}

            {/*
              ACEPTAR UNA INVITACION va FUERA del layout y sin `RequireAuth`.
              Quien llega por aqui puede no haber iniciado sesion todavia —la
              pagina se lo pide— y, aunque la inicie, NO tiene alcance sobre
              ningun cliente hasta que acepta: dentro del layout el selector de
              cliente estaria vacio y `RequireSeccion` lo echaria de todas las
              secciones. Pareceria que la invitacion no funciono.
            */}
            <Route path="/invitacion/:token" element={<InvitacionPage />} />

            <Route element={<RequireAuth />}>
              {/*
                EL TRASPASO, FUERA DE `AppLayout` Y A PANTALLA COMPLETA.

                Cesar: «una vez te logueas sólo te abre la parte de primeros
                pasos (sin todo lo otro) y te aparece sin nada de lo de arriba
                tampoco porque recién estás creando cada cosa».

                Estaba DENTRO del layout, así que quien entraba por primera vez
                veía el riel de siete secciones y la barra de alcance alrededor
                del cuestionario — con el selector de sociedad vacío, cuando
                crear la sociedad es el primer paso. Ver `TraspasoLayout`.
              */}
              <Route element={<TraspasoLayout />}>
                <Route path="primeros-pasos" element={<PrimerosPasosPage />} />
              </Route>

              {/*
                LA RAÍZ YA NO MANDA AL DASHBOARD SIN MIRAR. Decide con
                `pendientes_activos`, que el backend deriva de los datos. Ver
                `Entrada`. Va fuera del layout porque su trabajo es redirigir:
                montar el riel entero para descartarlo un instante después
                produce un parpadeo de la aplicación que no se llegó a usar.
              */}
              <Route index element={<Entrada />} />

              <Route element={<AppLayout />}>
                <Route path="dashboard" element={<DashboardPage />} />

                {/*
                  Primeros pasos del traspaso. Va al nivel de arriba y no dentro
                  de Contabilidad porque quien lo usa acaba de llegar de otra
                  plataforma y todavia no sabe que hay una seccion Contabilidad.
                  Y sin `RequireSeccion`: es la pantalla que explica el producto,
                  no una que opera sobre el.
                */}

                {/*
                  ACCESOS. Sin `RequireSeccion`: quien administra el equipo de
                  una empresa no tiene por que ser quien lleva su contabilidad,
                  y esconder esta pantalla detras de la seccion Contabilidad la
                  haria invisible justo para el rol que la necesita. El backend
                  exige `manage_access` en cada llamada.
                */}
                {/*
                  PENDIENTES. Sin `RequireSeccion`: quien deja un comentario es
                  justo el que MIRA —un gerente, un socio— y colgarlo de
                  Contabilidad o de Finanzas lo escondería del rol para el que se
                  construyó. Ver `components/Comentarios.tsx`.
                */}
                <Route path="pendientes" element={<PendientesPage />} />

                <Route path="accesos" element={<AccesosPage />} />

                {/* ----------------------------------------------- Auditoría */}
                <Route
                  path="auditoria"
                  element={
                    <RequireSeccion seccion="auditoria">
                      <AuditoriaPage />
                    </RequireSeccion>
                  }
                />

                {/* ------------------------------------------------- Holding */}
                {/*
                  Sin `RequireEntity`: el consolidado cruza TODAS las sociedades
                  por definicion, asi que exigir elegir una seria una puerta
                  inventada — y encima daria a entender que el numero cambia
                  segun cual elijas.
                */}
                <Route
                  path="holding"
                  element={
                    <RequireSeccion seccion="holding">
                      <HoldingLayout />
                    </RequireSeccion>
                  }
                >
                  <Route index element={<Navigate to="gestion" replace />} />
                  <Route path="consolidado" element={<ConsolidadoPage />} />
                  <Route path="analisis" element={<AnalisisPage />} />
                  <Route path="intercompania" element={<IntercompaniaPage />} />
                  <Route path="tesoreria" element={<TesoreriaPage />} />
                  <Route path="obligaciones" element={<ObligacionesPage />} />
                  {/* «Estructura» vive al pie de Consolidado. */}
                  <Route path="estructura" element={<Navigate to="../consolidado" replace />} />
                  {/*
                    LAS MISMAS PANTALLAS DE FINANZAS, con alcance de grupo y
                    el forecast sumado. `GestionDelGrupo` sólo cambia el
                    proveedor de filtros a `alcance="grupo"`. Ver su cabecera.
                  */}
                  <Route path="gestion" element={<GestionDelGrupo />}>
                    <Route index element={<Navigate to="pnl" replace />} />
                    <Route path="pnl" element={<PnlPage />} />
                    <Route path="caja" element={<CashFlowPage />} />
                    <Route path="forecast" element={<ForecastPage />} />
                    <Route path="cobranzas" element={<CobranzasPage />} />
                  </Route>
                </Route>

                {/* ---------------------------------------------- Finanzas */}
                <Route
                  path="finanzas"
                  element={
                    <RequireSeccion seccion="finanzas">
                      <FinanceLayout />
                    </RequireSeccion>
                  }
                >
                  <Route index element={<Navigate to="pnl" replace />} />
                  <Route path="pnl" element={<PnlPage />} />
                  <Route path="caja" element={<CashFlowPage />} />
                  {/*
                    EL DESGLOSE, UNA SOLA PESTAÑA. Las cuatro rutas anteriores
                    siguen vivas y redirigen con su cuenta puesta: están en la
                    guía, en marcadores y en el traspaso.
                  */}
                  <Route path="desglose" element={<DesgloseDeCuentasPage />} />
                  <Route path="revenue" element={<Navigate to="../desglose" replace />} />
                  <Route path="costo-ventas" element={<Navigate to={{ pathname: "../desglose", search: "?cuenta=costo-ventas" }} replace />} />
                  <Route path="gastos" element={<Navigate to={{ pathname: "../desglose", search: "?cuenta=gastos" }} replace />} />
                  <Route path="payroll" element={<Navigate to={{ pathname: "../desglose", search: "?cuenta=equipo" }} replace />} />
                  <Route path="forecast" element={<ForecastPage />} />
                  <Route path="declarado" element={<DeclaradoPage />} />
                  <Route path="cobranzas" element={<CobranzasPage />} />
                  {/*
                    LOS DOS CUADRES EN UNA PESTAÑA (punto 8 de la cuarta tanda).
                    Las rutas viejas siguen VIVAS y redirigen: están escritas en
                    el traspaso, en los enlaces de los pasos del onboarding y en
                    marcadores de Cesar. Una ruta que deja de existir rompe eso
                    en silencio y manda a un 404 que parece que la pantalla se
                    borró.
                  */}
                  <Route path="cuadres" element={<CuadresPage />} />
                  <Route path="conciliacion" element={<Navigate to="../cuadres" replace />} />
                  <Route path="hoja-vs-libro" element={<Navigate to="../cuadres" replace />} />
                  {/*
                    Bancos y tipos de cambio: los dos datos de BASE del modelo.
                    Sin bancos no hay tesoreria ni runway; sin tipos de cambio
                    el consolidado no se puede calcular en absoluto. Van en
                    Finanzas y no en Maestros porque es aqui donde se nota que
                    faltan.
                  */}
                  <Route path="datos" element={<DatosBasePage />} />
                  <Route path="que-va-a-finanzas" element={<QueVaAFinanzasPage />} />
                </Route>

                {/* ------------------------------------------ Contabilidad */}
                <Route
                  path="contabilidad"
                  element={
                    <RequireSeccion seccion="contabilidad">
                      <ContabilidadLayout />
                    </RequireSeccion>
                  }
                >
                  <Route index element={<Navigate to="revision" replace />} />
                  <Route path="revision" element={<ReviewQueuePage />} />

                  <Route path="libros" element={<BooksLayout />}>
                    <Route index element={<Navigate to="diario" replace />} />
                    <Route path="diario" element={<JournalPage />} />
                    <Route path="mayor" element={<LedgerPage />} />
                    <Route path="balance" element={<TrialBalancePage />} />
                    {/* «Análisis de cuenta» se fundió en el mayor; la cuenta viaja en `?cuenta=`. */}
                    <Route path="analisis" element={<AnalisisAlMayor />} />
                    <Route path="comparar" element={<CompareBooksPage />} />
                  </Route>

                  {/*
                    Los estados CONTABLES, en su propia seccion y no como dos
                    pestanas mas de "Libros". El diario y el mayor son material
                    de trabajo; estos tres son el entregable, y a un tercero se
                    le entrega esto, no el mayor.

                    Tampoco van en Finanzas: los de alli salen del modelo y
                    admiten criterio, estos salen de los asientos y tienen que
                    cuadrar. La pantalla que los compara ya existe
                    (/finanzas/hoja-vs-libro).
                  */}
                  <Route path="estados" element={<StatementsLayout />}>
                    <Route index element={<Navigate to="situacion" replace />} />
                    <Route path="situacion" element={<BalanceSheetPage />} />
                    <Route path="resultados" element={<IncomeStatementPage />} />
                    <Route path="flujo" element={<CashFlowStatementPage />} />
                  </Route>

                  {/*
                    LA PLANILLA. Con `RequireEntity`: una planilla que mezcla
                    cuatro sociedades no es la planilla de nadie — cada una
                    declara por separado, ante otra autoridad y con otra norma.
                    El backend también lo exige: `entity_id` es obligatorio.
                  */}
                  <Route
                    path="planilla"
                    element={
                      <RequireEntity que="su planilla">
                        <PlanillaPage />
                      </RequireEntity>
                    }
                  />

                  <Route path="maestros" element={<MastersLayout />}>
                    <Route index element={<Navigate to="sociedades" replace />} />
                    <Route path="sociedades" element={<SociedadesPage />} />
                    {/*
                      El plan del P&L. Sin `RequireEntity`: `account_group` es
                      del TENANT, no de una sociedad — el consolidado suma las
                      cuatro contra el mismo plan, y pedir una sociedad daria a
                      entender que cada una tiene el suyo.
                    */}
                    <Route path="plan-pl" element={<PlanGerencialPage />} />
                    {/*
                      Los PERIODOS. Sin `RequireEntity`: la pantalla muestra los
                      de todas las sociedades a la vez, que es como se ve de un
                      vistazo cual va atrasada. Cada apertura si elige sociedad.
                    */}
                    <Route path="periodos" element={<PeriodosPage />} />
                    <Route path="clientes" element={<ClientsPage />} />
                    <Route path="proveedores" element={<SuppliersPage />} />
                    <Route path="movimientos-bancarios" element={<MovimientosBancariosPage />} />
                    {/*
                      Estos tres maestros cuelgan de una SOCIEDAD, no del cliente:
                      el backend exige `entity_id` para darlos de alta y lo acepta
                      como filtro al listarlos. Sin sociedad elegida, la pantalla
                      lo pide en vez de mezclar las filas de todas las sociedades
                      —o de mandar la peticion y traducir un 422 con el nombre de
                      un campo que no esta en el formulario. Ver `RequireEntity`.

                      Clientes, proveedores y politicas NO van aca: `party` y las
                      politicas son del tenant entero, y exigir una sociedad para
                      verlos seria una puerta inventada.
                    */}
                    <Route
                      path="contratos"
                      element={
                        <RequireEntity que="sus contratos">
                          <ContractsPage />
                        </RequireEntity>
                      }
                    />
                    <Route
                      path="personas"
                      element={
                        <RequireEntity que="su gente">
                          <PeoplePage />
                        </RequireEntity>
                      }
                    />
                    <Route
                      path="centros-costo"
                      element={
                        <RequireEntity que="sus centros de costo">
                          <CostCentersPage />
                        </RequireEntity>
                      }
                    />
                    {/* «Políticas» se quitó (ver `MastersLayout`); la cadencia vigente se ve en Cobranzas. */}
                    <Route path="politicas" element={<Navigate to="/finanzas/cobranzas" replace />} />
                    <Route path="reglas" element={<TreatmentRulesPage />} />
                    {/*
                      CARGA POR ARCHIVO. Sin `RequireEntity` a proposito: las
                      siete plantillas son del CLIENTE y no de una sociedad
                      —«sociedades» justamente las crea, y bancos, tipos de
                      cambio y clientes son del tenant entero—, asi que exigir
                      una sociedad elegida seria una puerta inventada, y ademas
                      la primera que se cerraria en las narices de un cliente
                      nuevo, que todavia no tiene ninguna.
                    */}
                    <Route path="carga" element={<CargaPage />} />
                  </Route>

                  <Route path="declaraciones" element={<AccountantConsolePage />} />
                  {/*
                    EL EXPEDIENTE: los papeles de la empresa y lo que se presentó
                    de verdad. Cuelga de Contabilidad porque es donde vive lo
                    declarado, pero NO está gateado por alcance — ver el
                    comentario de su pestaña.
                  */}
                  <Route path="expediente" element={<ExpedientePage />} />
                  {/*
                    Los saldos arrastrados. Cuelgan de Declaraciones porque su
                    unico consumidor es el formulario: sin ellos, cinco casillas
                    del FV 621 salen en cero y eso no descuadra nada.
                  */}
                  <Route path="arrastres" element={<ArrastresPage />} />
                </Route>

                {/*
                  RUTAS ANTERIORES. Se conservan como redireccion y no se borran:
                  hay enlaces guardados, pestanas abiertas y capturas en tickets
                  que apuntan aca. Un 404 en una ruta que ayer funcionaba parece
                  una caida del sistema, no una reorganizacion del menu.
                */}
                <Route path="review" element={<Navigate to="/contabilidad/revision" replace />} />
                <Route path="books/*" element={<Navigate to="/contabilidad/libros" replace />} />
                <Route path="masters/*" element={<Navigate to="/contabilidad/maestros" replace />} />
                <Route path="accountant" element={<Navigate to="/contabilidad/declaraciones" replace />} />
                <Route path="caja" element={<Navigate to="/finanzas/caja" replace />} />

                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
